'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, BookOpen, Clock, Target, Lightbulb, Star, Eye, Brain, Heart, ArrowRight, Layout } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function UcKartPage() {
  const interpretationMethods = [
    {
      name: "Geçmiş - Şimdi - Gelecek",
      description: "En popüler yöntem. Durumun zaman çizgisini gösterir.",
      positions: [
        { position: "Sol", meaning: "Geçmiş", desc: "Bu duruma nasıl geldiniz, geçmiş etkileri" },
        { position: "Orta", meaning: "Şimdi", desc: "Mevcut durum, şu anki enerjiler" },
        { position: "Sağ", meaning: "Gelecek", desc: "Olası sonuçlar, ileriye dönük trendler" }
      ]
    },
    {
      name: "Durum - Eylem - Sonuç",
      description: "Problemi analiz ederek eylem planı sunar.",
      positions: [
        { position: "Sol", meaning: "Durum", desc: "Mevcut durumun analizi" },
        { position: "Orta", meaning: "Eylem", desc: "Yapmanız gereken adım" },
        { position: "Sağ", meaning: "Sonuç", desc: "Bu eylemi alırsanız olacaklar" }
      ]
    },
    {
      name: "Zihin - Beden - Ruh",
      description: "Bütünsel kişisel değerlendirme yapar.",
      positions: [
        { position: "Sol", meaning: "Zihin", desc: "Düşünceleriniz, mental durum" },
        { position: "Orta", meaning: "Beden", desc: "Fiziksel sağlık, pratik durum" },
        { position: "Sağ", meaning: "Ruh", desc: "Ruhsal enerji, iç dünya" }
      ]
    },
    {
      name: "Engel - Yol - Hedef",
      description: "Hedefe ulaşmak için yol haritası çizer.",
      positions: [
        { position: "Sol", meaning: "Engel", desc: "Karşılaştığınız zorluklar" },
        { position: "Orta", meaning: "Yol", desc: "Bu engeli aşma yöntemi" },
        { position: "Sağ", meaning: "Hedef", desc: "Ulaşabileceğiniz sonuç" }
      ]
    }
  ];

  const sampleQuestions = [
    "Bu ilişki nasıl gelişecek?",
    "Kariyer değişikliği yapmalı mıyım?",
    "Bu projeyi nasıl sonuçlandırabilirim?",
    "Finansal durumum nasıl iyileşir?",
    "Bu kararın sonuçları neler olacak?",
    "Kişisel gelişimim için ne yapmalıyım?"
  ];

  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-red-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Okuma Teknikleri</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Üç Kart Yayılımı</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot/interpretation-techniques"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Okuma Teknikleri
          </Link>
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-green-900/20 to-emerald-900/20 rounded-xl p-8 mb-8 border border-green-500/20">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-700 rounded-xl flex items-center justify-center">
              <BookOpen className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-100">Üç Kart Yayılımı</h1>
              <p className="text-green-300 font-medium">Başlangıç Seviyesi • 3 Kart</p>
            </div>
          </div>
          <p className="text-lg text-slate-200 leading-relaxed">
            En popüler ve çok yönlü tarot okuma yöntemi. Farklı şekillerde yorumlanabilir ve çoğu soruya
            etkili cevaplar verir. Tarot okumacılığının temel taşlarından biri.
          </p>
        </div>

        {/* Ana Bilgiler */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <Clock className="h-6 w-6 text-green-400" />
              <h3 className="text-lg font-semibold text-slate-100">Süre</h3>
            </div>
            <p className="text-slate-300">5-15 dakika</p>
            <p className="text-slate-400 text-sm">Orta detay seviyesi</p>
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <Target className="h-6 w-6 text-green-400" />
              <h3 className="text-lg font-semibold text-slate-100">Amaç</h3>
            </div>
            <p className="text-slate-300">Çok yönlü analiz</p>
            <p className="text-slate-400 text-sm">Durumsal değerlendirme</p>
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <Lightbulb className="h-6 w-6 text-green-400" />
              <h3 className="text-lg font-semibold text-slate-100">Seviye</h3>
            </div>
            <p className="text-slate-300">Başlangıç</p>
            <p className="text-slate-400 text-sm">Kolay öğrenilebilir</p>
          </div>
        </div>

        {/* Nasıl Yapılır */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Nasıl Yapılır?</h2>
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Hazırlık ve Niyetlenme</h3>
                  <p className="text-slate-300 mb-2">
                    Kartlarınızı karıştırırken sorunuzu zihniniızde net bir şekilde formüle edin.
                    Hangi yorumlama yöntemini kullanacağınızı önceden belirleyin.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Kart Dizimi</h3>
                  <p className="text-slate-300 mb-2">
                    Üç kartı yan yana, soldan sağa doğru dizin. Kartları yavaş yavaş,
                    sırayla çevirin ve her birini yerleştirirken pozisyonunu düşünün.
                  </p>
                  <div className="flex items-center justify-center gap-4 mt-4 p-4 bg-slate-700 rounded-lg">
                    <div className="w-16 h-24 bg-green-600 rounded-lg flex items-center justify-center">
                      <span className="text-white text-sm font-bold">1</span>
                    </div>
                    <div className="w-16 h-24 bg-green-600 rounded-lg flex items-center justify-center">
                      <span className="text-white text-sm font-bold">2</span>
                    </div>
                    <div className="w-16 h-24 bg-green-600 rounded-lg flex items-center justify-center">
                      <span className="text-white text-sm font-bold">3</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">3</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Bireysel Yorumlama</h3>
                  <p className="text-slate-300 mb-2">
                    Her kartı önce bağımsız olarak yorumlayın. Her kartın pozisyonundaki
                    anlamını düşünün ve not edin.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">4</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Bütünsel Değerlendirme</h3>
                  <p className="text-slate-300">
                    Üç kartı birlikte değerlendirin. Kartlar arasındaki bağlantıları,
                    temaları ve genel mesajı analiz edin.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Yorumlama Yöntemleri */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yorumlama Yöntemleri</h2>
          <div className="space-y-6">
            {interpretationMethods.map((method, index) => (
              <div key={index} className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-700 rounded-lg flex items-center justify-center">
                    <Layout className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-100">{method.name}</h3>
                    <p className="text-slate-400 text-sm">{method.description}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  {method.positions.map((pos, posIndex) => (
                    <div key={posIndex} className="bg-slate-700 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-bold">{posIndex + 1}</span>
                        </div>
                        <h4 className="font-semibold text-slate-100">{pos.meaning}</h4>
                      </div>
                      <p className="text-slate-300 text-sm">{pos.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Örnek Sorular */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Uygun Sorular</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sampleQuestions.map((question, index) => (
              <div key={index} className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                <div className="flex items-start gap-3">
                  <Eye className="h-4 w-4 text-green-400 mt-1 flex-shrink-0" />
                  <p className="text-slate-300 text-sm">{question}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Yorumlama İpuçları */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yorumlama İpuçları</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <Brain className="h-5 w-5 text-blue-400" />
                Analitik Yaklaşım
              </h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <p className="text-slate-300 font-medium text-sm">Kartları sayısal olarak değerlendirin</p>
                    <p className="text-slate-400 text-xs">Aynı sayıda kartlar var mı? Hangi sayılar tekrar ediyor?</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <p className="text-slate-300 font-medium text-sm">Takım dağılımını inceleyin</p>
                    <p className="text-slate-400 text-xs">Hangi element(ler) dominant? Bu ne anlama geliyor?</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <p className="text-slate-300 font-medium text-sm">Renk harmonisini gözlemleyin</p>
                    <p className="text-slate-400 text-xs">Kartlardaki genel renk tonları nasıl? Parlak mı kasvetli mi?</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <Heart className="h-5 w-5 text-purple-400" />
                Sezgisel Yaklaşım
              </h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <p className="text-slate-300 font-medium text-sm">Hikaye oluşturun</p>
                    <p className="text-slate-400 text-xs">Kartlar birlikte nasıl bir hikaye anlatıyor?</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <p className="text-slate-300 font-medium text-sm">Duygusal tonu hissedin</p>
                    <p className="text-slate-400 text-xs">Kartlara bakarken nasıl hissediyorsunuz?</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <p className="text-slate-300 font-medium text-sm">İlk izleniminize güvenin</p>
                    <p className="text-slate-400 text-xs">Kartları görünce aklınıza ilk gelen ne oldu?</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Yaygın Hatalar */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Kaçınılması Gereken Hatalar</h2>
          <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h4 className="text-amber-100 font-medium">Sadece bireysel kart anlamlarına odaklanmak</h4>
                  <p className="text-amber-200 text-sm">Kartları birlikte değerlendirin, bağlantıları arayın.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h4 className="text-amber-100 font-medium">Çok katı yorumlama yapmak</h4>
                  <p className="text-amber-200 text-sm">Kartların mesajları esnek olabilir, kontekste göre değişir.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h4 className="text-amber-100 font-medium">Pozisyon anlamlarını unutmak</h4>
                  <p className="text-amber-200 text-sm">Her kartın hangi pozisyonda olduğu önemlidir.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h4 className="text-amber-100 font-medium">Olumsuz kartlardan korkmak</h4>
                  <p className="text-amber-200 text-sm">Her kart fırsat ve öğreti içerir, olumsuz kartlar da dahil.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/interpretation-techniques/tek-kart"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Önceki: Tek Kart
          </Link>

          <Link
            href="/about/tarot/interpretation-techniques/celtic-cross"
            className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            Sonraki: Celtic Cross
            <ArrowRight className="h-4 w-4 ml-2" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}